// 9 Oct: the heartbeat. While this device holds a player session and the battle is on (lobby, question, reveal), the
// page tells the server it is still here with POST /me/seen: every 25 s while it is on screen, once at the start and
// once more each time it comes back to the front, never while hidden, never more than once in 10 s, and not at all
// from the podium on. A beat is skipped while an answer confirmed in the last 25 s already stands as a sign of life
// (the server counts answers the same way), so a phone that is answering sends fewer beats. Before this the server saw
// only joins, answers and reads, so a phone whose browser had died looked the same on the host and ops rosters as a
// player sitting a question out, and a seat had to be held for two minutes against a rejoin by email. Fire and forget:
// a failed beat is dropped without a retry and without a word on screen; the next one comes round in 25 s. The one
// refusal that is acted on is the seat having moved to another device (401 seat-moved, lib/seat): the beats stop and
// the page is told. The cost is one tiny request and one small write per player per 25 s at most.
//
// The goodbye (9 Oct): as the page is closed or navigated away from while the battle is on (pagehide), it sends
// POST /me/left once, so the rosters say "left" at once instead of "quiet" and then "gone". Hidden is not left: a page
// that is only hidden (the viewer flipped back to the Live) says nothing. It goes as a keepalive fetch, which outlives
// the page, with the session as a plain-text body and no headers of its own: a CORS-simple request, so a dying page
// never waits on a preflight (the route takes the session from the body when there is no authorization header).
// Dropped without a word if it fails, and a seat-moved refusal is ignored here (the page is going). When the page comes back (from the back-forward cache) the next beat goes at once,
// whatever the 10 s gap or a recent answer, so the server's last word is a sign of life again and the mark clears.
// Nothing after the podium. A killed browser or a dead signal sends no goodbye: those still show quiet, then gone.
import { useEffect, useRef } from 'react';
import type { BattleStatus } from '@shared/types';
import { api, apiUrl } from './api';
import { isSeatMoved } from './seat';

/** How often a beat goes while the page is on screen. The seat hold at join (api/src/handlers/join.ts) is sized from it. */
export const PRESENCE_EVERY_MS = 25_000;
/** The least time between two beats, however often the page is hidden and shown. */
export const PRESENCE_MIN_GAP_MS = 10_000;

const LIVE: ReadonlySet<BattleStatus> = new Set<BattleStatus>(['lobby', 'question', 'reveal']);
/** True while beats are wanted: the battle is known and not over. */
export const presenceWanted = (status: BattleStatus | undefined): boolean => status !== undefined && LIVE.has(status);

/** One beat: POST /me/seen with the session. 204 and no body on success. */
export const sendSeen = (token: string): Promise<unknown> => api('/me/seen', { method: 'POST', token });

/**
 * The goodbye: POST /me/left as the page goes away. keepalive lets the request outlive the page. The session is the
 * body, a plain string (text/plain), with no custom headers: a simple request needs no CORS preflight, which a page
 * that is closing might never see answered. The reply is never read (204 on success).
 */
export const sendLeft = (token: string): Promise<unknown> =>
  fetch(apiUrl('/me/left'), { method: 'POST', keepalive: true, body: token });

export interface PresenceOptions {
  token: string;
  /** The battle the session is for; a new battle starts afresh (the hook keys on it). */
  code: string;
  /** Asked before every beat: once true the beats stop for good. */
  isOver: () => boolean;
  /** This phone's clock when the server last confirmed an answer from it (0 or nothing for never): a beat within 25 s of it is skipped. */
  lastActivity?: () => number;
  /** Told once when a beat is refused because the seat moved to another device; the beats have stopped by then. */
  onMoved?: () => void;
  /** The request itself; replaced in tests. */
  send?: (token: string) => Promise<unknown>;
  /** The goodbye sent on pagehide; replaced in tests. */
  sendLeft?: (token: string) => Promise<unknown>;
}

/**
 * Starts the beats (and the goodbye on pagehide) for one session and returns the function that stops them. Safe to
 * call when the page is hidden: the first beat then waits for it to be shown.
 */
export function startPresence({ token, isOver, lastActivity, onMoved, send = sendSeen, sendLeft: goodbye = sendLeft }: PresenceOptions): () => void {
  let timer: number | undefined; let lastAt = Number.NEGATIVE_INFINITY; let stopped = false;
  let owed = false; // a goodbye went: the next beat goes at once, whatever the gap or a recent answer
  const visible = () => document.visibilityState !== 'hidden';
  const clear = () => { if (timer !== undefined) window.clearInterval(timer); timer = undefined; };
  const stop = () => {
    stopped = true; clear();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide); window.removeEventListener('pageshow', onPageShow);
  };
  const beat = () => {
    if (stopped) return;
    if (isOver()) { stop(); return; }
    if (!visible()) return;
    const now = Date.now();
    if (!owed && now - lastAt < PRESENCE_MIN_GAP_MS) return;
    if (!owed && lastActivity && now - lastActivity() < PRESENCE_EVERY_MS) return;
    owed = false; lastAt = now;
    try {
      void Promise.resolve(send(token)).catch((e: unknown) => { if (isSeatMoved(e) && !stopped) { stop(); onMoved?.(); } });
    } catch { /* a beat that could not even be sent is dropped like one that failed */ }
  };
  const onVisibility = () => {
    clear();
    if (stopped || !visible()) return;
    beat();
    if (!stopped) timer = window.setInterval(beat, PRESENCE_EVERY_MS);
  };
  // Closed or navigated away from (never on visibilitychange: hidden is not left). Sent whatever the visibility, as
  // browsers hide the page just before pagehide.
  const onPageHide = () => {
    if (stopped) return;
    if (isOver()) { stop(); return; }
    owed = true;
    try { void Promise.resolve(goodbye(token)).catch(() => { /* dropped: the page is going */ }); } catch { /* not even sent: dropped */ }
  };
  // Back from the back-forward cache: start again as if shown (some browsers send no visibilitychange for it).
  const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) onVisibility(); };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide); window.addEventListener('pageshow', onPageShow);
  onVisibility();
  return stop;
}

/**
 * The beats and the goodbye for the game page: on while the session and a live status are known, stopped the moment
 * the status is podium or ended, and started afresh for another session or battle. `lastActivity` and `onMoved` are
 * read through refs, so a page may hand fresh functions on every render without restarting the beats.
 */
export function usePresence(p: { token?: string; code?: string; status?: BattleStatus; lastActivity?: () => number; onMoved?: () => void }): void {
  const statusRef = useRef(p.status);
  const activity = useRef(p.lastActivity); const moved = useRef(p.onMoved);
  useEffect(() => { statusRef.current = p.status; activity.current = p.lastActivity; moved.current = p.onMoved; });
  const { token, code } = p;
  const wanted = Boolean(token) && presenceWanted(p.status);
  useEffect(() => {
    if (!wanted || !token) return;
    return startPresence({
      token, code: code ?? '', isOver: () => !presenceWanted(statusRef.current),
      lastActivity: () => activity.current?.() ?? 0, onMoved: () => moved.current?.(),
    });
  }, [token, code, wanted]);
}
